import { useState, useEffect, useCallback, useRef } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Input } from '@/components/ui/input'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Checkbox } from '@/components/ui/checkbox'
import { DatePickerWithRange } from '@/components/ui/date-range-picker'
import type { DateRange } from 'react-day-picker'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Bug, Filter, RotateCcw, AlertTriangle, Lock } from 'lucide-react'
import { BugReportTable } from '@/components/bug-reports/BugReportTable'
import { BugReportDetailView } from '@/components/bug-reports/BugReportDetailView'
import { useProgramContext } from '@/context/ProgramContext'
import { addDays } from 'date-fns'
import { motion } from 'framer-motion'
import { apiClient } from '@/lib/api'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
	Pagination,
	PaginationContent,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
	PaginationEllipsis,
} from '@/components/ui/pagination'
import { cn } from '@/lib/utils'
import { useTranslation } from '@/hooks/useTranslation'

interface BugReport {
	id: string
	description: string
	status: string
	severity: string
	created_at: string
	updated_at: string
	screenshot_url: string | null
	user_metadata: Record<string, unknown> | null
	reporter_email: string | null
	reporter_name: string | null
	assignee_user_id: string | null
	widget_id: string
	program_id: string
	widget?: { name: string }
	program?: { name: string }
}

interface BugReportApiResponse {
	bugs: BugReport[]
	totalMatchingBugReports: number
	totalLifetimeBugReports: number
	viewableLimit: number
	hiddenCount: number
	isPro: boolean
	hasMore: boolean
	currentPage: number
	itemsPerPage: number
	totalPages: number
}

const getSeverityOptions = (t: (key: string) => string) => [
	{ label: t('bugReports.allSeverities'), value: 'all' },
	{ label: t('bugReports.critical'), value: 'critical' },
	{ label: t('bugReports.high'), value: 'high' },
	{ label: t('bugReports.medium'), value: 'medium' },
	{ label: t('bugReports.low'), value: 'low' },
]

const getStatusOptions = (t: (key: string) => string) => [
	{ label: t('bugReports.statusNew'), value: 'new' },
	{ label: t('bugReports.statusInProgress'), value: 'in_progress' },
	{ label: t('bugReports.statusResolved'), value: 'resolved' },
	{ label: t('bugReports.statusClosed'), value: 'closed' },
	{ label: t('bugReports.statusArchived'), value: 'archived' },
]

const getSortOptions = (t: (key: string) => string) => [
	{ label: t('bugReports.sortOldestFirst'), value: 'created_at_asc' },
	{ label: t('bugReports.sortLastUpdated'), value: 'updated_at_desc' },
	{ label: t('bugReports.sortSeverityHighToLow'), value: 'severity_desc' },
	{ label: t('bugReports.sortStatus'), value: 'status_asc' },
]

export default function BugReports() {
	const { t } = useTranslation()
	const severityOptions = getSeverityOptions(t)
	const statusOptions = getStatusOptions(t)
	const sortOptions = getSortOptions(t)
	const {
		currentProgram,
		programId,
		loading: programLoading,
	} = useProgramContext()
	const [search, setSearch] = useState('')
	const [selectedSeverities, setSelectedSeverities] = useState<string[]>([
		'critical',
		'high',
		'medium',
		'low',
	])
	const [selectedStatuses, setSelectedStatuses] = useState<string[]>([
		'new',
		'in_progress',
	])
	const [sort, setSort] = useState('updated_at_desc')
	const [dateRange, setDateRange] = useState<DateRange | undefined>({
		from: new Date('2025-01-01'),
		to: new Date(),
	})
	const [selectedBugId, setSelectedBugId] = useState<string | null>(null)
	const [searchParams, setSearchParams] = useSearchParams()
	const navigate = useNavigate()
	const itemsPerPage = 10

	const currentPage = Math.max(
		parseInt(searchParams.get('page') || '1', 10) || 1,
		1
	)

	useEffect(() => {
		const bugIdFromUrl = searchParams.get('bugId')
		if (bugIdFromUrl && bugIdFromUrl !== selectedBugId) {
			setSelectedBugId(bugIdFromUrl)
		} else if (!bugIdFromUrl && selectedBugId) {
			setSelectedBugId(null)
		}
	}, [searchParams, selectedBugId])

	const {
		data: apiResponse,
		isLoading,
		error,
		refetch,
	} = useQuery<BugReportApiResponse, Error>({
		queryKey: [
			'bugs',
			programId,
			{
				search,
				selectedSeverities,
				selectedStatuses,
				sort,
				dateRange,
				currentPage,
				itemsPerPage,
			},
		],
		queryFn: async () => {
			if (!programId)
				return {
					bugs: [],
					totalMatchingBugReports: 0,
					totalLifetimeBugReports: 0,
					viewableLimit: 0,
					hiddenCount: 0,
					isPro: false,
					hasMore: false,
					currentPage: 1,
					itemsPerPage,
					totalPages: 0,
				}

			const params = new URLSearchParams({
				programId,
				limit: String(itemsPerPage),
				page: String(currentPage),
			})
			if (search) params.append('search', search)
			if (selectedSeverities.length > 0)
				params.append('severities', selectedSeverities.join(','))
			if (selectedStatuses.length > 0)
				params.append('statuses', selectedStatuses.join(','))

			if (sort) params.append('sort', sort)
			if (dateRange?.from)
				params.append('fromDate', dateRange.from.toISOString())
			if (dateRange?.to)
				params.append(
					'toDate',
					new Date(dateRange.to.setHours(23, 59, 59, 999)).toISOString()
				)

			return apiClient<BugReportApiResponse>(
				`/api/bugs/protected?${params.toString()}`
			)
		},
		enabled: !!programId && !programLoading,
		placeholderData: keepPreviousData,
	})

	useEffect(() => {
		const prevProgramId = localStorage.getItem('prevProgramIdForBugFilter')
		if (programId && prevProgramId !== programId) {
			setSearchParams(
				(prev) => {
					const params = new URLSearchParams(prev)
					params.set('page', '1')
					return params
				},
				{ replace: true }
			)
			localStorage.setItem('prevProgramIdForBugFilter', programId)
		} else if (!programId) {
			localStorage.removeItem('prevProgramIdForBugFilter')
		}
		// setSearchParams reference is unstable (changes on every URL change) and must not be a dep
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [programId])

	const isFirstFilterRenderRef = useRef(true)
	useEffect(() => {
		if (isFirstFilterRenderRef.current) {
			isFirstFilterRenderRef.current = false
			return
		}
		setSearchParams(
			(prev) => {
				const params = new URLSearchParams(prev)
				params.set('page', '1')
				return params
			},
			{ replace: true }
		)
		// setSearchParams reference is unstable (changes on every URL change) and must not be a dep
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dateRange, selectedSeverities, selectedStatuses, search])

	const handlePageChange = useCallback(
		(newPage: number) => {
			setSearchParams(
				(prev) => {
					const newSearchQuery = new URLSearchParams(prev)
					newSearchQuery.set('page', String(newPage))
					return newSearchQuery
				},
				{ replace: true }
			)
		},
		[setSearchParams]
	)

	const handleReset = () => {
		setSearch('')
		setSelectedSeverities(['critical', 'high', 'medium', 'low'])
		setSelectedStatuses(['new', 'in_progress'])
		setSort('updated_at_desc')
		setDateRange({ from: new Date('2025-01-01'), to: new Date() })
		const newParams = new URLSearchParams()
		if (searchParams.has('bugId'))
			newParams.set('bugId', searchParams.get('bugId')!)
		newParams.set('page', '1')
		setSearchParams(newParams, { replace: true })
	}

	const toggleStatus = (status: string) => {
		setSelectedStatuses((prev) => {
			const newStatuses = prev.includes(status)
				? prev.filter((s) => s !== status)
				: [...prev, status]
			return newStatuses
		})
	}

	const toggleSeverity = (severity: string) => {
		setSelectedSeverities((prev) => {
			const newSeverities = prev.includes(severity)
				? prev.filter((s) => s !== severity)
				: [...prev, severity]
			return newSeverities
		})
	}

	if (programLoading) return <Skeleton className="h-screen w-full" />

	if (error) {
		return (
			<div className="container py-6 text-center">
				<AlertTriangle className="mx-auto h-12 w-12 text-destructive" />
				<h2 className="mt-4 text-xl font-semibold">
					{t('bugReports.errorLoadingBugReports')}
				</h2>
				<p className="mt-2 text-muted-foreground">{error.message}</p>
				<Button onClick={() => refetch()} className="mt-4">
					{t('bugReports.retry')}
				</Button>
			</div>
		)
	}

	const rawBugs = apiResponse?.bugs || []
	const bugsToDisplay =
		selectedStatuses.length === 0 || selectedSeverities.length === 0
			? []
			: rawBugs
	const totalPages = apiResponse?.totalPages || 1

	return (
		<div className="container py-6 space-y-6">
			<motion.div
				initial={{ opacity: 0, y: -20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.3 }}
				className="bg-gradient-to-r from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 rounded-xl p-6 shadow-sm border border-border/50">
				<div className="flex items-center gap-3">
					<div className="p-2 bg-primary/10 rounded-lg">
						<Bug className="h-5 w-5 text-primary" />
					</div>
					<div>
						<h1 className="text-xl md:text-2xl font-semibold text-foreground">
							{t('bugReports.bugReports')}
						</h1>
						<p className="text-sm text-muted-foreground mt-0.5">
							{t('bugReports.viewAndManageIssues', { programName: currentProgram?.name || t('bugReports.yourProgram') })}
						</p>
					</div>
				</div>
			</motion.div>

			<Card className="p-4">
				<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
					<Input
						placeholder={t('bugReports.searchBugs')}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="xl:col-span-1"
					/>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="outline" className="w-full justify-between">
								{t('bugReports.severity')} ({selectedSeverities.length}/
								{severityOptions.filter((opt) => opt.value !== 'all').length})
								<span className="sr-only">{t('bugReports.toggleSeverityMenu')}</span>
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent className="w-56 p-3">
							<div className="space-y-3">
								<div className="flex gap-2">
									<Button
										variant="outline"
										size="sm"
										className="flex-1 text-xs"
										onClick={() => {
											const allSeverities = severityOptions.filter(
												(opt) => opt.value !== 'all'
											).map((opt) => opt.value)
											setSelectedSeverities(allSeverities)
										}}>
										{t('bugReports.selectAll')}
									</Button>
									<Button
										variant="outline"
										size="sm"
										className="flex-1 text-xs"
										onClick={() => {
											setSelectedSeverities([])
										}}>
										{t('bugReports.unselectAll')}
									</Button>
								</div>
								<div className="space-y-2">
									{severityOptions.filter(
										(option) => option.value !== 'all'
									).map((option) => (
										<div
											key={option.value}
											className="flex items-center space-x-2">
											<Checkbox
												id={`severity-${option.value}`}
												checked={selectedSeverities.includes(option.value)}
												onCheckedChange={() => toggleSeverity(option.value)}
											/>
											<label
												htmlFor={`severity-${option.value}`}
												className="text-sm font-medium cursor-pointer flex-1">
												{option.label}
											</label>
										</div>
									))}
								</div>
							</div>
						</DropdownMenuContent>
					</DropdownMenu>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="outline" className="w-full justify-between">
								{t('bugReports.status')} ({selectedStatuses.length}/{statusOptions.length})
								<span className="sr-only">{t('bugReports.toggleStatusMenu')}</span>
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent className="w-56 p-3">
							<div className="space-y-3">
								<div className="flex gap-2">
									<Button
										variant="outline"
										size="sm"
										className="flex-1 text-xs"
										onClick={() => {
											const allStatuses = statusOptions.map((opt) => opt.value)
											setSelectedStatuses(allStatuses)
										}}>
										{t('bugReports.selectAll')}
									</Button>
									<Button
										variant="outline"
										size="sm"
										className="flex-1 text-xs"
										onClick={() => {
											setSelectedStatuses([])
										}}>
										{t('bugReports.unselectAll')}
									</Button>
								</div>
								<div className="space-y-2">
									{statusOptions.map((option) => (
										<div
											key={option.value}
											className="flex items-center space-x-2">
											<Checkbox
												id={`status-${option.value}`}
												checked={selectedStatuses.includes(option.value)}
												onCheckedChange={() => toggleStatus(option.value)}
											/>
											<label
												htmlFor={`status-${option.value}`}
												className="text-sm font-medium cursor-pointer flex-1">
												{option.label}
											</label>
										</div>
									))}
								</div>
							</div>
						</DropdownMenuContent>
					</DropdownMenu>

					<div className="md:col-span-2 lg:col-span-4 xl:col-span-1">
						<DatePickerWithRange
							date={dateRange}
							setDate={(newRange) => setDateRange(newRange)}
						/>
					</div>
					<div className="xl:col-start-5 flex justify-end items-center">
						<Button variant="outline" onClick={handleReset} size="sm">
							<RotateCcw className="mr-2 h-4 w-4" /> {t('bugReports.reset')}
						</Button>
					</div>
				</div>
			</Card>

			{apiResponse && !apiResponse.isPro && apiResponse.hiddenCount > 0 && (
				<Alert
					variant="default"
					className="mb-6 bg-amber-50 border-amber-300 dark:bg-amber-900/30 dark:border-amber-700">
					<Lock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
					<AlertTitle className="font-semibold text-amber-700 dark:text-amber-300">
						{t('bugReports.viewAllBugReports')}
					</AlertTitle>
					<AlertDescription className="text-amber-600 dark:text-amber-400">
						{t('bugReports.collectedBugReports', {
							total: apiResponse.totalLifetimeBugReports,
							limit: apiResponse.viewableLimit,
							hidden: apiResponse.hiddenCount,
						})}
						<Button
							variant="link"
							className="p-0 h-auto text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200 underline ml-1 font-medium"
							onClick={() => navigate('/settings?tab=billing')}>
							{t('bugReports.upgradeToProToSeeAll')}
						</Button>
					</AlertDescription>
				</Alert>
			)}

			{isLoading && !apiResponse ? (
				<BugReportTable
					bugs={[]}
					isLoading={true}
					sort={sort}
					onSortChange={setSort}
				/>
			) : bugsToDisplay.length > 0 ||
			  (!apiResponse?.isPro &&
					apiResponse?.hiddenCount &&
					apiResponse.hiddenCount > 0 &&
					bugsToDisplay.length < itemsPerPage) ? (
				<BugReportTable
					bugs={bugsToDisplay}
					isLoading={isLoading && !!apiResponse}
					onBugSelect={(bugId) => {
						setSelectedBugId(bugId)
						setSearchParams(
							(prev) => {
								const newSearchQuery = new URLSearchParams(prev)
								if (bugId) {
									newSearchQuery.set('bugId', bugId)
								} else {
									newSearchQuery.delete('bugId')
								}
								return newSearchQuery
							},
							{ replace: true }
						)
					}}
					isPro={apiResponse?.isPro}
					hiddenCount={apiResponse?.hiddenCount || 0}
					itemsPerPage={itemsPerPage}
					sort={sort}
					onSortChange={setSort}
				/>
			) : (
				<Card>
					<CardContent className="text-center py-12 text-muted-foreground">
						{t('bugReports.noBugReportsFound')}
					</CardContent>
				</Card>
			)}

			{apiResponse &&
				selectedStatuses.length > 0 &&
				selectedSeverities.length > 0 &&
				(totalPages > 1 || bugsToDisplay.length > 0) && (
				<div className="flex flex-wrap items-center justify-end gap-4">
					{totalPages > 1 && (
				<Pagination>
					<PaginationContent>
						<PaginationItem>
							<PaginationPrevious
								onClick={() =>
									currentPage > 1 &&
									!isLoading &&
									handlePageChange(currentPage - 1)
								}
								aria-disabled={currentPage === 1 || isLoading}
								className={cn(
									(currentPage === 1 || isLoading) &&
										'pointer-events-none opacity-50'
								)}
								href="#"
							/>
						</PaginationItem>

						{Array.from({ length: totalPages }, (_, i) => i + 1).map(
							(pageNum) => {
								const showPageLink =
									pageNum === 1 ||
									pageNum === totalPages ||
									(pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
								const showEllipsisStart =
									pageNum === currentPage - 2 &&
									currentPage > 3 &&
									totalPages > 5
								const showEllipsisEnd =
									pageNum === currentPage + 2 &&
									currentPage < totalPages - 2 &&
									totalPages > 5

								if (showPageLink) {
									return (
										<PaginationItem key={pageNum}>
											<PaginationLink
												onClick={() => !isLoading && handlePageChange(pageNum)}
												isActive={currentPage === pageNum}
												className={cn(
													isLoading && 'pointer-events-none opacity-50'
												)}
												href="#">
												{pageNum}
											</PaginationLink>
										</PaginationItem>
									)
								}
								if (showEllipsisStart || showEllipsisEnd) {
									return (
										<PaginationItem key={`ellipsis-${pageNum}`}>
											<PaginationEllipsis />
										</PaginationItem>
									)
								}
								return null
							}
						)}

						<PaginationItem>
							<PaginationNext
								onClick={() =>
									currentPage < totalPages &&
									!isLoading &&
									handlePageChange(currentPage + 1)
								}
								aria-disabled={currentPage === totalPages || isLoading}
								className={cn(
									(currentPage === totalPages || isLoading) &&
										'pointer-events-none opacity-50'
								)}
								href="#"
							/>
						</PaginationItem>
					</PaginationContent>
				</Pagination>
					)}
				</div>
			)}

			{selectedBugId && (
				<BugReportDetailView
					bugId={selectedBugId}
					onClose={() => {
						if (selectedBugId) {
							setSelectedBugId(null)
							setSearchParams(
								(prev) => {
									const newSearchQuery = new URLSearchParams(prev)
									newSearchQuery.delete('bugId')
									return newSearchQuery
								},
								{ replace: true }
							)
						}
					}}
				/>
			)}
		</div>
	)
}
